import React from 'react';
import { errorAnalytics } from '../../core/error-util';
import config, { isDevelopment, isProd } from '../../config';
import errorIcon from '../../assets/images/error-icon.svg';
import styles from './style.css';
import { dispatchErrorLoad } from '../../analytics/analytics-helper';
import { NodeNotFoundError } from '../../core/errors';
import { getAccountFinderUrl } from '../../flows/shared/utils';

export const ErrorBoundaryContext = React.createContext(() => {});

const DebugInfo = ({ error, info }) => (
  <>
    {isDevelopment() && (
      <div>
        <code>{error.stack}</code>
        {Boolean(info) && <code>{info}</code>}
      </div>
    )}
  </>
);

export const NOT_FOUND_MSG = () => (
  <div className="error-link">
    The page you were looking for does not exist. You can create another account{' '}
    <a data-no-warn href={getAccountFinderUrl()}>
      here.
    </a>
    <style jsx>{styles}</style>
  </div>
);

export const DEFAULT_MSG = () => (
  <>
    <div className="error-msg">
      A technical error occurred while creating your new account.
    </div>
    <div className="error-link">
      To continue, please{' '}
      <a
        data-no-warn
        href={`${config(
          'returnUrlPrefix'
        )}personal-investing/new-account/setup/return-journey/email`}>
        try again.
      </a>
    </div>
    <style jsx>{styles}</style>
  </>
);

export const NASUError = ({ error, info, message }) => (
  <div className="error-wrapper">
    <img src={errorIcon} alt="error icon" />
    <DebugInfo error={error} info={info} />
    <div className="error-info">
      {message()}
      <div>
        For assistance, please call a T. Rowe Price Investment Specialist at
        1-855-434-9918.
      </div>
      <br />
    </div>
    <style jsx>{styles}</style>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }

  componentDidCatch(error, info) {
    this.triggerError({ error, info });
  }

  static getDerivedStateFromError = error => ({ error, hasError: true });

  triggerError = ({ error, info }, hideErrorScreen = false) => {
    if (hideErrorScreen) {
      this.setState(false);
    } else {
      this.setState({ error });
    }

    // Send error adobe analytics
    dispatchErrorLoad(errorAnalytics(error));

    // eslint-disable-next-line no-console
    if (!isProd()) console.error(`${error.message}:`, error, info);
  };

  render() {
    const { error, hasError } = this.state;
    const { children } = this.props;

    return (
      <ErrorBoundaryContext.Provider value={this.triggerError}>
        {hasError || error ? (
          <NASUError
            error={error}
            message={
              error instanceof NodeNotFoundError ? NOT_FOUND_MSG : DEFAULT_MSG
            }
          />
        ) : (
          children
        )}
      </ErrorBoundaryContext.Provider>
    );
  }
}

export default ErrorBoundary;
